import { useEffect, useState } from "react";
import type { Interviewer } from "../../hooks";
import { useInterviewerMutations } from "../../hooks";
import { CloseIcon } from "../Icons";

interface InterviewerAssignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  termId: string | null;
  /** アサイン済み判定にIDしか使わないので、IDを持つ型なら何でも渡せるようにする */
  termInterviewers: readonly { interviewerId: string }[];
  allInterviewers: Interviewer[];
  /** ダイアログを開いたときにチェック済みにするインタビュアーID（例: デフォルト担当の一括提案） */
  initialSelectedInterviewerIds?: string[];
  onAssigned: () => void;
}

export function InterviewerAssignDialog({
  open,
  onOpenChange,
  termId,
  termInterviewers,
  allInterviewers,
  initialSelectedInterviewerIds = [],
  onAssigned,
}: InterviewerAssignDialogProps) {
  const { assignInterviewerToTerm } = useInterviewerMutations();
  const [selectedInterviewerIds, setSelectedInterviewerIds] = useState<
    string[]
  >([]);

  useEffect(() => {
    if (open) {
      setSelectedInterviewerIds(initialSelectedInterviewerIds);
    }
  }, [open, initialSelectedInterviewerIds]);

  if (!open) return null;

  const handleAssignInterviewers = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!termId || selectedInterviewerIds.length === 0) {
      alert("インタビュアーを選択してください");
      return;
    }

    try {
      for (const interviewerId of selectedInterviewerIds) {
        const result = await assignInterviewerToTerm(termId, interviewerId);
        if (result && !result.success) {
          alert(result.errorMessage || "インタビュアーの追加に失敗しました");
          return;
        }
      }
      onOpenChange(false);
      setSelectedInterviewerIds([]);
      onAssigned();
      alert("インタビュアーを追加しました");
    } catch {
      alert("インタビュアーの追加に失敗しました");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="max-h-[90vh] w-[90%] max-w-[600px] overflow-auto rounded-xl bg-white p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-6 flex items-center justify-between">
          <h3 className="font-bold text-lg">インタビュアーをアサイン</h3>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="cursor-pointer border-none bg-none p-1"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>
        <form
          onSubmit={handleAssignInterviewers}
          className="flex flex-col gap-4"
        >
          <div>
            <label className="mb-2 block font-medium text-sm">
              インタビュアーを選択
            </label>
            <div className="max-h-[300px] overflow-y-auto rounded-md border border-[var(--color-border-default)] p-4">
              {allInterviewers.map((interviewer) => {
                const isAssigned = termInterviewers.some(
                  (i) => i.interviewerId === interviewer.interviewerId,
                );
                return (
                  <label
                    key={interviewer.interviewerId}
                    className={`flex items-center gap-2 rounded p-2 ${isAssigned ? "cursor-default opacity-50" : "cursor-pointer"}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedInterviewerIds.includes(
                        interviewer.interviewerId,
                      )}
                      disabled={isAssigned}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedInterviewerIds([
                            ...selectedInterviewerIds,
                            interviewer.interviewerId,
                          ]);
                        } else {
                          setSelectedInterviewerIds(
                            selectedInterviewerIds.filter(
                              (id) => id !== interviewer.interviewerId,
                            ),
                          );
                        }
                      }}
                    />
                    <span>
                      {interviewer.name || interviewer.email}
                      {isAssigned && (
                        <span className="ml-2 text-[var(--color-text-secondary)] text-xs">
                          (既にアサイン済み)
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
          <button
            type="submit"
            disabled={selectedInterviewerIds.length === 0}
            className={`rounded-md border-none px-4 py-2.5 font-medium text-sm text-white ${selectedInterviewerIds.length === 0 ? "cursor-default bg-gray-400" : "cursor-pointer bg-[var(--color-primary-500)]"}`}
          >
            追加
          </button>
        </form>
      </div>
    </div>
  );
}
